<template>
  <div class="chart-report">
    <div class="top-left-container">
      <button @click="showPopup()" class="btn btn-mantraz">Create Chart</button>
    </div>

    <div v-if="isLoading" class="loading-indicator">
      Loading data...
    </div>
    <!-- Anda bisa menambahkan pesan error di sini jika ada error dari store -->
    <div v-else>
      <div v-if="isPopupVisible" class="popup-overlay">
        <div class="popup-content">
          <h4>{{ isEditing ? 'Edit Chart' : 'Create New Chart' }}</h4>

          <!-- Date Range Selector (menggunakan v-date-picker) - Dipindahkan ke atas -->
          <div class="form-group mb-3">
            <label for="date-range" class="form-label">Select Date Range:</label>
            <v-date-picker v-model="selectedDateRange" is-range mode="datetime" :masks="masks" is24hr>
              <template #default="{ inputValue, inputEvents, isDragging }">
                <input
                  class="form-control bg-light border rounded date-input"
                  :class="isDragging ? 'text-muted' : 'text-dark'"
                  :value="inputValue.start && inputValue.end ? `${inputValue.start} - ${inputValue.end}` : ''"
                  v-on="inputEvents.start"
                  placeholder="e.g., 2024-01-01 00:00 - 2024-01-31 23:59"
                />
              </template>
            </v-date-picker>
          </div>

          <!-- Dropdown untuk memilih Asset -->
          <div class="form-group mb-3">
            <label for="asset-select" class="form-label">Select Asset:</label>
            <select
              id="asset-select"
              class="form-control"
              v-model="selectedAssetId"
              @change="onAssetChange"
            >
              <option value="" disabled>Please select an asset</option>
              <option
                v-for="(asset) in allAssets"
                :key="asset.massId"
                :value="asset.massId"
              >
                {{ asset.massName }}
              </option>
            </select>
          </div>

          <!-- Toggle untuk memilih Device (multiple selection) dan Data Poin -->
          <div class="form-group mb-3">
            <label class="form-label">Select Device(s) and Data Points:</label>
            <div v-if="selectedAssetId && availableDevices.length > 0" class="device-selection-container">
              <div class="device-list">
                <div
                  v-for="(device) in availableDevices"
                  :key="device.devcId"
                  class="device-item"
                  :class="{ 'selected-device': isDeviceSelected(device.devcId) }"
                >
                  <div class="device-main-toggle" @click="handleDeviceToggle(device)">
                    <label class="form-check-label">
                      {{ device.devcName }} ({{ device.devcUniqueId }})
                    </label>
                  </div>

                  <div v-if="isDeviceSelected(device.devcId)" class="data-points-list">
                    <div class="datapoint-buttons mb-2">
                        <button @click.stop="checkAllDataPoints(device)" class="btn btn-xs btn-outline-primary">Check All</button>
                        <button @click.stop="uncheckAllDataPoints(device)" class="btn btn-xs btn-outline-secondary">Uncheck All</button>
                    </div>

                    <div
                      v-for="(dataPoint) in deviceTypeDataMap[device.devcType]"
                      :key="`${device.devcId}-${dataPoint}`"
                      class="data-point-item"
                    >
                      <input
                        type="checkbox"
                        :id="`data-${device.devcId}-${dataPoint}`"
                        :value="dataPoint"
                        :checked="isDataPointSelected(device.devcId, dataPoint)"
                        @change="toggleDataPoint(device.devcId, dataPoint)"
                        class="form-check-input-sm"
                      />
                      <label :for="`data-${device.devcId}-${dataPoint}`" class="form-check-label-sm">
                        {{ splitCamelCase(dataPoint) }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p v-else-if="!selectedAssetId" class="text-muted small">Select an asset first to see devices.</p>
            <p v-else class="text-muted small">No devices available for this asset.</p>
          </div>

          <!-- Save and Close Buttons - Diatur bersebelahan -->
          <div class="d-flex justify-content-end mt-4 button-group">
            <button
              @click="saveChartConfig"
              class="btn btn-success mr-2"
              :disabled="!isFormValid || isLoadingSavedChartData"
            >
              <span v-if="isLoadingSavedChartData" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span v-else>{{ isEditing ? 'Update Chart' : 'Save Chart' }}</span>
            </button>
            <button @click="closePopup" class="btn btn-danger">Close</button>
          </div>

          <div v-if="showSaveConfirmation" class="alert alert-success mt-3" role="alert">
            Chart configuration saved successfully!
          </div>
        </div>
      </div>

      <!-- Saved Chart Configurations Display as Cards -->
      <div v-if="savedCharts.length > 0" class="p-3">
        <draggable v-model="savedCharts" @end="onDragEnd" ghost-class="ghost" class="saved-charts-grid">
          <div v-for="(chart) in savedCharts" :key="chart.id" class="saved-chart-card" :class="`chart-width-${chart.width || 'full'}`">
            <div class="card-header">
              <!-- [MODIFIED] Wrapper untuk info di sebelah kiri header -->
              <div class="header-info">
                <div class="date-range-display">
                  <span class="font-weight-bold">Range: </span>
                  <span>{{ chart.dateRange.start ? formatDateTime(chart.dateRange.start, 'DD-MM-YYYY HH:mm') : 'N/A' }}</span>
                  <span> - </span>
                  <span>{{ chart.dateRange.end ? formatDateTime(chart.dateRange.end, 'DD-MM-YYYY HH:mm') : 'N/A' }}</span>
                </div>
                <!-- [MOVED] Judul Asset dipindahkan ke sini -->
                <p class="asset-title mb-0"><strong>Asset:</strong> {{ chart.asset ? chart.asset.massName : 'N/A' }}</p>
              </div>
              <div class="card-actions">
                <button @click="toggleChartWidth(chart)" class="btn btn-sm btn-outline-secondary mr-2" title="Toggle chart width">
                  <i :class="chart.width === 'half' ? 'ri-fullscreen-line' : 'ri-layout-column-line'"></i>
                </button>
                <button @click="refreshChartData(chart)" class="btn btn-sm btn-outline-info mr-2" :disabled="chart.isLoadingRefresh">
                  <span v-if="chart.isLoadingRefresh" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-else><i class="ri-refresh-line"></i> Refresh</span>
                </button>
                <button @click="editChart(chart)" class="btn btn-sm btn-outline-warning mr-2">
                  <i class="ri-edit-line"></i> Edit
                </button>
                <button @click="deleteChart(chart.id)" class="btn btn-sm btn-outline-danger">
                  <i class="ri-delete-bin-line"></i> Delete
                </button>
              </div>
            </div>
            <div class="card-body">
              <!-- [REMOVED] Judul Asset dihapus dari sini -->
              <div class="chart-display-area">
                <div v-if="chart.combinedChart && chart.combinedChart.series && chart.combinedChart.series.length > 0">
                  <apexchart
                    :key="`${chart.id}-${chart.width}`"
                    type="line"
                    height="375"
                    :options="chart.combinedChart.options"
                    :series="chart.combinedChart.series"
                  ></apexchart>
                </div>
                <p v-else class="text-muted small">No chart data available for this configuration.</p>
              </div>
            </div>
          </div>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-alert, no-restricted-globals */
import moment from 'moment';
import { formatDateTime } from '@/util/date';
import SeriesSvc from '@/services/SeriesSvc';
import VueApexCharts from 'vue-apexcharts';
import draggable from 'vuedraggable';

const dataIntervalOptions = [
  { label: 'By Minute', value: 'MINUTE', seconds: 60 },
  { label: 'Hourly', value: 'HOUR', seconds: 3600 },
  { label: 'Daily', value: 'DAY', seconds: 86400 },
  { label: 'Weekly', value: 'WEEK', seconds: 604800 },
  { label: 'Monthly', value: 'MONTH', seconds: 2592000 },
];

function getSeconds(timeUnit) {
  let seconds = 0;
  dataIntervalOptions.forEach((option) => {
    if (option.value === timeUnit) {
      seconds = option.seconds;
    }
  });
  return seconds;
}

function autoMaxData(dateParam) {
  let secondsLength = 86400;
  if (dateParam.range && dateParam.range.start && dateParam.range.end) {
    secondsLength = dateParam.range.end - dateParam.range.start;
  }

  const maxDataLimit = 50;
  const minuteInSeconds = getSeconds('MINUTE');

  dateParam.aggregatedUnit = 'MINUTE';

  const expectedDataLengthInMinutes = secondsLength / minuteInSeconds;

  if (expectedDataLengthInMinutes > maxDataLimit) {
    dateParam.aggregatedLength = Math.ceil(expectedDataLengthInMinutes / maxDataLimit);
  } else {
    dateParam.aggregatedLength = 1;
  }

  return dateParam;
}

export default {
  name: 'ChartReport',
  components: {
    apexchart: VueApexCharts,
    draggable,
  },
  data() {
    return {
      isPopupVisible: false,
      isLoading: false,
      selectedAssetId: '',
      selectedDateRange: {
        start: moment().subtract(6, 'hours').toDate(),
        end: moment().toDate(),
      },
      masks: {
        input: 'DD-MM-YYYY HH:mm',
      },
      selectedChartData: [],
      selectedIntervalUnit: 'minute',
      selectedIntervalLength: 1,
      deviceTypeDataMap: {
        flowmeter: ['massFlowrate', 'density', 'temperature', 'volumeFlowrate', 'massTotal', 'volumeTotal', 'massInventory', 'volumeInventory'],
        gateway: ['cpuUsage', 'memoryFree', 'spaceFree'],
        gps: ['track', 'satellites', 'altitude', 'geoidal', 'latitude', 'longitude', 'speed', 'distance'],
        rpm: ['port', 'starboard'],
        ae: ['ae 1', 'ae 2', 'ae 3'],
        temperature: ['port', 'starboard'],
        power: ['onAc', 'batteryFail', 'batteryVoltage'],
      },
      savedCharts: [],
      showSaveConfirmation: false,
      isLoadingSavedChartData: false,
      isEditing: false,
      editingChartId: null,
      db: null,
    };
  },
  computed: {
    allAssets() {
      return this.$store.state.asset.assets || [];
    },
    selectedAsset() {
      return this.allAssets.find((asset) => asset.massId === this.selectedAssetId);
    },
    availableDevices() {
      if (this.selectedAsset && this.selectedAsset.devices) {
        return this.selectedAsset.devices;
      }
      return [];
    },
    chartFormData() {
      return {
        asset: this.selectedAsset,
        devicesData: this.selectedChartData,
        dateRange: this.selectedDateRange,
        intervalUnit: this.selectedIntervalUnit,
        intervalLength: this.selectedIntervalLength,
      };
    },
    isFormValid() {
      const isAssetSelected = !!this.selectedAssetId;
      const isDateRangeSelected = this.selectedDateRange && this.selectedDateRange.start && this.selectedDateRange.end;
      const isAnyDeviceAndDataPointSelected = this.selectedChartData.some((device) => device.selectedDataPoints.length > 0);
      const isIntervalValid = this.selectedIntervalLength > 0;

      return isAssetSelected && isDateRangeSelected && isAnyDeviceAndDataPointSelected && isIntervalValid;
    },
  },
  methods: {
    formatDateTime,
    showPopup(isEdit = false) {
      if (!isEdit) {
        this.selectedAssetId = '';
        this.selectedChartData = [];
        this.selectedDateRange = {
          start: moment().subtract(6, 'hours').toDate(),
          end: moment().toDate(),
        };
        this.selectedIntervalUnit = 'minute';
        this.selectedIntervalLength = 1;
        this.isEditing = false;
        this.editingChartId = null;
        this.showSaveConfirmation = false;
      }
      this.isPopupVisible = true;
    },
    closePopup() {
      this.isPopupVisible = false;
    },
    onAssetChange() {
      this.selectedChartData = [];
    },
    isDeviceSelected(devcId) {
      return this.selectedChartData.some((item) => item.devcId === devcId);
    },
    handleDeviceToggle(device) {
      const index = this.selectedChartData.findIndex((item) => item.devcId === device.devcId);
      if (index > -1) {
        this.selectedChartData.splice(index, 1);
      } else {
        this.selectedChartData.push({
          devcId: device.devcId,
          devcName: device.devcName,
          devcUniqueId: device.devcUniqueId,
          devcType: device.devcType,
          selectedDataPoints: [],
        });
      }
    },
    isDataPointSelected(devcId, dataPoint) {
      const deviceEntry = this.selectedChartData.find((item) => item.devcId === devcId);
      return deviceEntry ? deviceEntry.selectedDataPoints.includes(dataPoint) : false;
    },
    toggleDataPoint(devcId, dataPoint) {
      const deviceEntry = this.selectedChartData.find((item) => item.devcId === devcId);
      if (deviceEntry) {
        const dataPointIndex = deviceEntry.selectedDataPoints.indexOf(dataPoint);
        if (dataPointIndex > -1) {
          deviceEntry.selectedDataPoints.splice(dataPointIndex, 1);
        } else {
          deviceEntry.selectedDataPoints.push(dataPoint);
        }
      }
    },
    checkAllDataPoints(device) {
      const deviceEntry = this.selectedChartData.find((item) => item.devcId === device.devcId);
      if (deviceEntry) {
        const allDataPoints = this.deviceTypeDataMap[device.devcType] || [];
        deviceEntry.selectedDataPoints = [...allDataPoints];
      }
    },
    uncheckAllDataPoints(device) {
      const deviceEntry = this.selectedChartData.find((item) => item.devcId === device.devcId);
      if (deviceEntry) {
        deviceEntry.selectedDataPoints = [];
      }
    },
    generateCombinedChartSeries(allRawFetchedData, devicesDataConfig) {
      const combinedChartSeries = [];
      const allTimestamps = [];

      allRawFetchedData.forEach((deviceRawData) => {
        const seriesArray = Array.isArray(deviceRawData.data.series) ? deviceRawData.data.series : Object.values(deviceRawData.data);
        seriesArray.forEach((entry) => {
          if (entry && entry.timestamp && !allTimestamps.includes(entry.timestamp)) {
            allTimestamps.push(entry.timestamp);
          }
        });
      });
      allTimestamps.sort((a, b) => a - b);

      devicesDataConfig.forEach((deviceConfig) => {
        const rawDeviceData = allRawFetchedData.find((item) => item.devcId === deviceConfig.devcId);

        if (rawDeviceData) {
          const rawSeriesArrayForDevice = Array.isArray(rawDeviceData.data.series) ? rawDeviceData.data.series : Object.values(rawDeviceData.data);

          deviceConfig.selectedDataPoints.forEach((dataPointKey) => {
            const seriesData = allTimestamps.map((ts) => {
              let value = null;
              const entry = rawSeriesArrayForDevice.find(
                (s) => s.timestamp === ts && s.deviceId === deviceConfig.devcUniqueId,
              );

              if (entry) {
                if (deviceConfig.devcType === 'rpm' || deviceConfig.devcType === 'temperature') {
                  if (entry.type && entry.type.toLowerCase() === dataPointKey.toLowerCase()) {
                    value = deviceConfig.devcType === 'rpm' ? entry.rpm : entry.temperature;
                  }
                } else if (deviceConfig.devcType === 'ae') {
                  const aeNo = parseInt(dataPointKey.replace('ae ', ''), 10) - 1;
                  if (entry.no === aeNo) {
                    value = entry.rpm;
                  }
                } else {
                  value = entry[dataPointKey];
                }
              }
              return [ts * 1000, value];
            });

            combinedChartSeries.push({
              name: `${deviceConfig.devcName} - ${dataPointKey}`,
              data: seriesData.filter((item) => item[1] !== null && item[1] !== undefined),
            });
          });
        }
      });

      return combinedChartSeries;
    },
    getChartOptions() {
      return {
        chart: {
          height: 375,
          type: 'line',
          zoom: { enabled: true },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
            animateGradually: { enabled: true, delay: 150 },
            dynamicAnimation: { enabled: true, speed: 350 },
          },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
        },
        xaxis: {
          type: 'datetime',
          labels: { format: 'HH:mm' },
        },
        yaxis: {
          title: { text: 'Value' },
        },
        tooltip: {
          x: { format: 'DD/MM/YY HH:mm' },
        },
        stroke: { curve: 'smooth', width: 2 },
        dataLabels: { enabled: false },
        title: {
          text: 'Combined Chart Data',
          align: 'left',
          style: { fontSize: '14px', fontWeight: 'bold', color: '#333' },
        },
        grid: {
          borderColor: '#e7e7e7',
          row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 },
        },
        markers: { size: 4 },
      };
    },
    async saveChartConfig() {
      if (this.isFormValid) {
        this.isLoadingSavedChartData = true;
        try {
          const dateParam = {
            range: {
              start: this.selectedDateRange.start.getTime() / 1000,
              end: this.selectedDateRange.end.getTime() / 1000,
            },
            aggregatedUnit: 'MINUTE',
            aggregatedLength: 1,
          };
          const autoAggregatedParams = autoMaxData(dateParam);

          const chartConfigToSave = {
            ...this.chartFormData,
            id: this.isEditing ? this.editingChartId : Date.now(),
            fetchedData: [],
            combinedChart: { series: [], options: {} },
            intervalUnit: autoAggregatedParams.aggregatedUnit.toLowerCase(),
            intervalLength: autoAggregatedParams.aggregatedLength,
            width: 'full',
          };

          if (this.isEditing) {
            const existingChart = this.savedCharts.find((c) => c.id === this.editingChartId);
            chartConfigToSave.order = existingChart ? existingChart.order : this.savedCharts.length;
            chartConfigToSave.width = existingChart ? (existingChart.width || 'full') : 'full';
          } else {
            chartConfigToSave.order = this.savedCharts.length;
          }

          const allRawFetchedDataPromises = chartConfigToSave.devicesData
            .filter((device) => device.selectedDataPoints.length > 0)
            .map(async (device) => {
              const params = {
                params: {
                  device: [device.devcUniqueId],
                  aggregatedUnit: chartConfigToSave.intervalUnit.toUpperCase(),
                  aggregatedLength: chartConfigToSave.intervalLength,
                },
              };
              const response = await SeriesSvc.getAggregatedDataByPeriod(
                device.devcType,
                moment(chartConfigToSave.dateRange.start).unix(),
                moment(chartConfigToSave.dateRange.end).unix(),
                params,
              );
              return {
                devcId: device.devcId,
                devcUniqueId: device.devcUniqueId,
                devcName: device.devcName,
                devcType: device.devcType,
                data: response.data.data.series || response.data.data,
              };
            });

          const allRawFetchedData = await Promise.all(allRawFetchedDataPromises);

          chartConfigToSave.combinedChart.series = this.generateCombinedChartSeries(allRawFetchedData, chartConfigToSave.devicesData);
          chartConfigToSave.combinedChart.options = this.getChartOptions();

          if (this.isEditing) {
            const index = this.savedCharts.findIndex((chart) => chart.id === this.editingChartId);
            if (index !== -1) {
              this.$set(this.savedCharts, index, chartConfigToSave);
            }
            await this.updateChartInDb(chartConfigToSave);
          } else {
            this.savedCharts.push(chartConfigToSave);
            await this.addChartToDb(chartConfigToSave);
          }

          this.showSaveConfirmation = true;
          setTimeout(() => { this.showSaveConfirmation = false; }, 3000);

          this.closePopup();
        } catch (error) {
          console.error('Error saving chart config or fetching data:', error);
          alert('Failed to save chart configuration or fetch data. Please try again.');
        } finally {
          this.isLoadingSavedChartData = false;
        }
      } else {
        alert('Please fill in all required fields (Asset, Date Range, and at least one Device with data points).');
      }
    },
    async refreshChartData(chartToRefresh) {
      this.$set(chartToRefresh, 'isLoadingRefresh', true);
      try {
        const dateParam = {
          range: {
            start: chartToRefresh.dateRange.start.getTime() / 1000,
            end: chartToRefresh.dateRange.end.getTime() / 1000,
          },
          aggregatedUnit: 'MINUTE',
          aggregatedLength: 1,
        };
        const autoAggregatedParams = autoMaxData(dateParam);

        chartToRefresh.intervalUnit = autoAggregatedParams.aggregatedUnit.toLowerCase();
        chartToRefresh.intervalLength = autoAggregatedParams.aggregatedLength;

        const allRawFetchedDataPromises = chartToRefresh.devicesData
          .filter((device) => device.selectedDataPoints.length > 0)
          .map(async (device) => {
            const params = {
              params: {
                device: [device.devcUniqueId],
                aggregatedUnit: chartToRefresh.intervalUnit.toUpperCase(),
                aggregatedLength: chartToRefresh.intervalLength,
              },
            };
            const response = await SeriesSvc.getAggregatedDataByPeriod(
              device.devcType,
              moment(chartToRefresh.dateRange.start).unix(),
              moment(chartToRefresh.dateRange.end).unix(),
              params,
            );
            return {
              devcId: device.devcId,
              devcUniqueId: device.devcUniqueId,
              devcName: device.devcName,
              devcType: device.devcType,
              data: response.data.data.series || response.data.data,
            };
          });

        const allRawFetchedData = await Promise.all(allRawFetchedDataPromises);
        const combinedSeries = this.generateCombinedChartSeries(allRawFetchedData, chartToRefresh.devicesData);
        const combinedOptions = this.getChartOptions();

        this.$set(chartToRefresh, 'combinedChart', { series: combinedSeries, options: combinedOptions });
        await this.updateChartInDb(chartToRefresh);
        alert(`Data for chart '${chartToRefresh.asset ? chartToRefresh.asset.massName : 'N/A'}' refreshed successfully!`);
      } catch (error) {
        console.error('Error refreshing chart data:', error);
        alert(`Failed to refresh data for chart: ${chartToRefresh.asset ? chartToRefresh.asset.massName : 'N/A'}`);
      } finally {
        this.$set(chartToRefresh, 'isLoadingRefresh', false);
      }
    },
    editChart(chart) {
      this.isEditing = true;
      this.editingChartId = chart.id;
      this.selectedAssetId = chart.asset ? chart.asset.massId : '';
      this.selectedDateRange = { ...chart.dateRange };
      this.selectedChartData = chart.devicesData.map((deviceData) => ({
        ...deviceData,
        selectedDataPoints: [...deviceData.selectedDataPoints],
      }));
      this.selectedIntervalUnit = chart.intervalUnit || 'minute';
      this.selectedIntervalLength = chart.intervalLength || 1;
      this.showPopup(true);
    },
    async deleteChart(chartId) {
      if (confirm('Are you sure you want to delete this chart configuration?')) {
        try {
          this.savedCharts = this.savedCharts.filter((chart) => chart.id !== chartId);
          await this.deleteChartFromDb(chartId);
          await this.onDragEnd();
          alert('Chart configuration deleted successfully!');
        } catch (error) {
          console.error('Error deleting chart from DB:', error);
          alert('Failed to delete chart configuration. Please try again.');
        }
      }
    },
    async onDragEnd() {
      const updatedCharts = this.savedCharts.map((chart, index) => {
        chart.order = index;
        return chart;
      });

      try {
        await Promise.all(updatedCharts.map((chart) => this.updateChartInDb(chart)));
        console.log('Chart order updated successfully.');
      } catch (error) {
        console.error('Failed to save new chart order:', error);
      }
    },
    async toggleChartWidth(chart) {
      const newWidth = chart.width === 'half' ? 'full' : 'half';
      this.$set(chart, 'width', newWidth);
      try {
        await this.updateChartInDb(chart);
      } catch (error) {
        console.error('Failed to save chart width:', error);
        this.$set(chart, 'width', chart.width === 'half' ? 'full' : 'half');
      }
    },
    openDb() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('ChartDB', 1);
        request.onupgradeneeded = (event) => {
          this.db = event.target.result;
          this.db.createObjectStore('charts', { keyPath: 'id' });
        };
        request.onsuccess = (event) => {
          this.db = event.target.result;
          resolve(this.db);
        };
        request.onerror = (event) => {
          reject(event.target.errorCode);
        };
      });
    },
    addChartToDb(chartConfig) {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction(['charts'], 'readwrite');
        const objectStore = transaction.objectStore('charts');
        const request = objectStore.add(chartConfig);
        request.onsuccess = resolve;
        request.onerror = (event) => reject(event.target.errorCode);
      });
    },
    getChartsFromDb() {
      return new Promise((resolve, reject) => {
        if (!this.db) {
          resolve([]);
          return;
        }
        const transaction = this.db.transaction(['charts'], 'readonly');
        const objectStore = transaction.objectStore('charts');
        const request = objectStore.getAll();
        request.onsuccess = (event) => {
          const charts = event.target.result;
          charts.sort((a, b) => (a.order || 0) - (b.order || 0));
          resolve(charts);
        };
        request.onerror = (event) => reject(event.target.errorCode);
      });
    },
    updateChartInDb(chartConfig) {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction(['charts'], 'readwrite');
        const objectStore = transaction.objectStore('charts');
        const request = objectStore.put(chartConfig);
        request.onsuccess = resolve;
        request.onerror = (event) => reject(event.target.errorCode);
      });
    },
    deleteChartFromDb(chartId) {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction(['charts'], 'readwrite');
        const objectStore = transaction.objectStore('charts');
        const request = objectStore.delete(chartId);
        request.onsuccess = resolve;
        request.onerror = (event) => reject(event.target.errorCode);
      });
    },
    splitCamelCase(text) {
      const result = text.replace(/([A-Z])/g, ' $1').toLowerCase();
      return result;
    },
  },
  async mounted() {
    this.isLoading = true;
    try {
      await this.openDb();
      this.savedCharts = await this.getChartsFromDb();
      await this.$store.dispatch('asset/getAssetsData');
      await this.$store.dispatch('asset/getAssetsDevices');
    } catch (error) {
      console.error('Error fetching data or opening IndexedDB:', error);
    } finally {
      this.isLoading = false;
    }
  },
};
</script>

<style scoped>
.top-left-container {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;
  margin-left: 10px;
}

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  width: 700px;
  max-width: 95%;
  max-height: 80vh;
  overflow-y: auto;
  text-align: center;
  color: #333;
}

.popup-content h2 {
  margin-top: 0;
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 1rem;
  text-align: left;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #4a4a4a;
}

.form-control {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.date-input {
  width: 100%;
  cursor: pointer;
}

.loading-indicator, .error-message {
  text-align: center;
  margin-top: 20px;
  padding: 10px;
  border-radius: 5px;
}

.loading-indicator {
  background-color: #e0f7fa;
  color: #00796b;
}

.error-message {
  background-color: #ffebee;
  color: #c62828;
}

.device-selection-container {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  background-color: #fcfcfc;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.device-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 15px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 8px;
}

.device-item {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.08);
  transition: all 0.2s ease-in-out;
  overflow: hidden;
}

.device-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.device-item.selected-device {
  border-color: #007bff;
  box-shadow: 0 4px 12px rgba(0,123,255,0.2);
}

.device-main-toggle {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  cursor: pointer;
  background-color: #f0f0f0;
  border-bottom: 1px solid #e0e0e0;
  position: relative;
}

.selected-device .device-main-toggle {
  background-color: #007bff;
  color: white;
}

.device-item label.form-check-label {
  margin-bottom: 0;
  cursor: pointer;
  flex-grow: 1;
  word-break: break-word;
  line-height: 1.3;
  font-weight: 500;
  color: #333;
}

.selected-device .device-main-toggle label.form-check-label {
  color: white;
}

.data-points-list {
  margin-top: 0;
  padding: 10px 15px;
  width: 100%;
  text-align: left;
  background-color: #fcfcfc;
}

.selected-device .data-points-list {
  background-color: #e6f2ff;
}

.datapoint-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.datapoint-buttons .btn-xs {
  padding: 2px 8px;
  font-size: 0.75rem;
  border-radius: 10px;
}

.data-point-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.data-point-item:last-child {
  margin-bottom: 0;
}

.form-check-input-sm {
  display: inline-block;
  opacity: 1;
  position: static;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 3px;
  border: 1px solid #ced4da;
  vertical-align: middle;
}

.form-check-input-sm:checked {
  background-color: #28a745;
  border-color: #28a745;
}

.form-check-label-sm {
  margin-bottom: 0;
  font-size: 0.85em;
  color: #666;
  cursor: pointer;
  line-height: 1.2;
}

.button-group {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
}

.saved-charts-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 15px;
  background-color: #f0f2f5;
  border-radius: 8px;
}

.saved-chart-card {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  cursor: move;
}

.chart-width-full {
    width: 100%;
}

.chart-width-half {
    width: calc(50% - 10px); /* 10px adalah setengah dari gap */
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.saved-chart-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

.saved-chart-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background-color: #f8f8f8;
  border-bottom: 1px solid #eee;
  font-size: 0.9em;
  color: #555;
}

/* [ADDED] Styling untuk wrapper info di header */
.header-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
}

.asset-title {
  margin-top: 4px; /* Memberi sedikit jarak dari range tanggal */
  font-size: 0.95em;
}

.saved-chart-card .card-header .date-range-display {
  flex-grow: 1;
  text-align: left;
}

.saved-chart-card .card-header .card-actions {
  display: flex;
  gap: 8px;
}

.saved-chart-card .card-header .refresh-btn {
  font-size: 0.8em;
  padding: 4px 10px;
  border-radius: 15px;
}

.saved-chart-card .card-body {
  padding: 15px;
  text-align: left;
  flex-grow: 1;
}

.saved-chart-card .card-body p {
  margin-bottom: 0.5rem;
  font-size: 0.95em;
}

.chart-display-area {
  /* [MODIFIED] Menghapus padding-top karena body sudah punya padding */
}
</style>
